import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Truck, Shield, ShieldCheck, Key, Mail, Smartphone, X, Lock, Check } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });
  const [loading, setLoading] = useState(false);
  
  // Forced password change (first login / admin reset) - modal cannot be dismissed
  const [showForcePwModal, setShowForcePwModal] = useState(false);
  const [forcePwToken, setForcePwToken] = useState('');
  const [forcePwNew, setForcePwNew] = useState('');
  const [forcePwConfirm, setForcePwConfirm] = useState('');
  const [forcePwLoading, setForcePwLoading] = useState(false);

  // Password reset states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  // MFA states
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [trustDevice, setTrustDevice] = useState(true);
  const [rememberMe, setRememberMe] = useState(true);
  
  // Email MFA states
  const [mfaMethod, setMfaMethod] = useState('app'); // 'app' or 'email'
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  
  // MFA Setup method choice
  const [mfaSetupMethod, setMfaSetupMethod] = useState('app'); // 'app' or 'email' for initial setup

  // Whether the Authenticator App option is enabled system-wide (Settings > User Admin > MFA).
  // When off, email is the only MFA path for every user — set from /auth/login's response.
  const [authenticatorEnabled, setAuthenticatorEnabled] = useState(true);

  // Check for reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    if (token) {
      setResetToken(token);
      setShowResetModal(true);
    }
  }, []);

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success('If the email exists, a reset link has been sent to your inbox');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.response?.data?.detail || 'Error sending reset email';
      toast.error(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmNewPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }

    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
      toast.success('Password reset successful! You can now login with your new password');
      setShowResetModal(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setResetToken('');
      // Clear URL
      window.history.replaceState({}, document.title, '/');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Error resetting password');
    } finally {
      setResetLoading(false);
    }
  };

  const handleForcePasswordChange = async () => {
    if (!forcePwNew || !forcePwConfirm) {
      toast.error('Please fill in both fields');
      return;
    }
    if (forcePwNew !== forcePwConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (forcePwNew.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }

    setForcePwLoading(true);
    try {
      await axios.post(`${API}/auth/force-change-password`, {
        token: forcePwToken,
        new_password: forcePwNew,
      });

      const chosenPassword = forcePwNew;
      const loginEmail = formData.email;
      toast.success('Password updated. Continuing to sign in...');
      setShowForcePwModal(false);
      setForcePwToken('');
      setForcePwNew('');
      setForcePwConfirm('');
      setFormData({ ...formData, password: chosenPassword });

      // Sign in again with the new password: this runs the normal flow, so MFA
      // (trusted device, authenticator app, email code, first-time setup) appears as usual.
      await handleSubmit(null, { email: loginEmail, password: chosenPassword });
    } catch (error) {
      console.error('Force password change error:', error);
      toast.error(error.response?.data?.detail || 'Error changing password');
      const status = error.response?.status;
      if (status === 401 || status === 429) {
        // Session expired or locked out: the user has to sign in again to get a fresh one
        setShowForcePwModal(false);
        setForcePwToken('');
        setForcePwNew('');
        setForcePwConfirm('');
      }
    } finally {
      setForcePwLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaCode || mfaCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setMfaLoading(true);
    try {
      // Use different endpoint based on MFA method
      const endpoint = mfaMethod === 'email' 
        ? `${API}/auth/mfa/verify-email-code`
        : `${API}/auth/mfa/verify`;
        
      const response = await axios.post(endpoint, {
        token: mfaToken,
        code: mfaCode,
        trust_device: trustDevice,
        user_agent: navigator.userAgent
      });
      
      // Store device token if provided
      if (response.data.device_token) {
        localStorage.setItem('mfa_device_token', response.data.device_token);
      }
      
      toast.success('Login successful!');
      setShowMfaModal(false);
      setMfaCode('');
      setMfaMethod('app');
      setEmailCodeSent(false);
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      console.error('MFA verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid MFA code');
    } finally {
      setMfaLoading(false);
    }
  };
  
  const handleSendEmailCode = async () => {
    setSendingEmailCode(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/send-email-code`, {
        token: mfaToken
      });

      toast.success(response.data.message || 'Code sent to your email');
      setEmailCodeSent(true);
    } catch (error) {
      console.error('Error sending email code:', error);
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingEmailCode(false);
    }
  };
  
  // Send email code during MFA SETUP (first time)
  const handleSendSetupEmailCode = async () => {
    setSendingEmailCode(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/send-email-code`, {
        token: mfaToken
      });

      toast.success(response.data.message || 'Code sent to your email');
      setEmailCodeSent(true);
    } catch (error) {
      console.error('Error sending email code:', error);
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingEmailCode(false);
    }
  };
  
  // Verify email code during MFA SETUP (first time) - this enables email MFA for the user
  const handleVerifySetupEmailCode = async () => {
    if (!mfaSetupCode || mfaSetupCode.length < 6) {
      toast.error('Please enter the 6-digit code from your email');
      return;
    }
    
    setMfaLoading(true);
    try {
      // Verify the email code - this also enables email MFA for the user
      const response = await axios.post(`${API}/auth/mfa/verify-email-code`, {
        token: mfaToken,
        code: mfaSetupCode,
        trust_device: trustDevice,
        user_agent: navigator.userAgent
      });
      
      // Store device token if provided
      if (response.data.device_token) {
        localStorage.setItem('mfa_device_token', response.data.device_token);
      }
      
      toast.success('Email MFA enabled successfully!');
      setShowMfaSetup(false);
      setMfaSetupData(null);
      setMfaSetupCode('');
      setEmailCodeSent(false);
      setMfaSetupMethod('app');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      console.error('Email MFA setup error:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };
  
  const handleMfaSetupConfirm = async () => {
    if (!mfaSetupCode || mfaSetupCode.length < 6) {
      toast.error('Please enter a valid 6-digit code from your authenticator app');
      return;
    }
    
    setMfaLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/confirm`, {
        code: mfaSetupCode
      }, {
        headers: { Authorization: `Bearer ${mfaToken}` }
      });
      
      setBackupCodes(response.data.backup_codes);
      toast.success('MFA enabled successfully!');
      
      // Now verify with the same code to complete login
      const verifyResponse = await axios.post(`${API}/auth/mfa/verify`, {
        token: mfaToken,
        code: mfaSetupCode
      });
      
      // Show backup codes before completing login
      if (response.data.backup_codes && response.data.backup_codes.length > 0) {
        setMfaSetupData({ ...mfaSetupData, showBackupCodes: true, backupCodes: response.data.backup_codes, verifyToken: verifyResponse.data.access_token, verifyUser: verifyResponse.data.user });
      } else {
        onLogin(verifyResponse.data.access_token, verifyResponse.data.user);
      }
    } catch (error) {
      console.error('MFA setup error:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setMfaLoading(false);
    }
  };
  
  const completeMfaSetupLogin = () => {
    if (mfaSetupData?.verifyToken && mfaSetupData?.verifyUser) {
      const token = mfaSetupData.verifyToken;
      const user = mfaSetupData.verifyUser;
      setShowMfaSetup(false);
      setMfaSetupData(null);
      setMfaSetupCode('');
      onLogin(token, user);
    }
  };

  const handleSubmit = async (e, override) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Register new account
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }

        await axios.post(`${API}/auth/register-request`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
        });

        toast.success('Account request submitted! You will receive an email once approved.');
        setIsRegister(false);
        setFormData({ email: '', password: '', confirmPassword: '', fullName: '' });
      } else {
        // Login - check for existing device token
        const storedDeviceToken = localStorage.getItem('mfa_device_token');
        
        // Clear any previous MFA setup data
        setMfaSetupData(null);
        setMfaSetupCode('');
        
        const response = await axios.post(`${API}/auth/login`, {
          username: override?.email ?? formData.email,
          password: override?.password ?? formData.password,
          remember_me: rememberMe,
          device_token: storedDeviceToken || null
        });
        
        // Account flagged for a forced password change: show the locked modal and stop here.
        // MFA is offered on the next login call, after the password has been changed.
        if (response.data.password_change_required) {
          setForcePwToken(response.data.access_token);
          setForcePwNew('');
          setForcePwConfirm('');
          setShowForcePwModal(true);
          return;
        }

        // Check if MFA is required
        if (response.data.mfa_required) {
          setMfaToken(response.data.access_token);
          // Authenticator App can be disabled system-wide (Settings > User Admin) — when off,
          // email is the only path, for both existing and first-time MFA users.
          const authEnabled = response.data.authenticator_enabled !== false;
          setAuthenticatorEnabled(authEnabled);
          if (response.data.mfa_enabled) {
            // User has MFA set up - show verification modal
            setMfaMethod(authEnabled ? 'app' : 'email');
            setShowMfaModal(true);
          } else {
            // User needs to set up MFA first
            setMfaSetupMethod(authEnabled ? 'app' : 'email');
            setShowMfaSetup(true);
            if (authEnabled) {
              // Fetch MFA setup data (QR code / secret)
              try {
                const setupResponse = await axios.post(`${API}/auth/mfa/setup`, {}, {
                  headers: { Authorization: `Bearer ${response.data.access_token}` }
                });
                setMfaSetupData(setupResponse.data);
              } catch (err) {
                toast.error('Failed to initialize MFA setup');
              }
            } else {
              // No authenticator app to configure — go straight to the email-only setup screen
              setMfaSetupData({});
            }
          }
        } else {
          toast.success('Login successful!');
          onLogin(response.data.access_token, response.data.user);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || (isRegister ? 'Registration failed' : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center p-4 pt-4 md:pt-6" style={{ backgroundColor: '#ffffff' }}>
      <Card className="w-full max-w-md shadow-2xl border-2 bg-white" style={{ borderColor: '#ee7203' }} data-testid="login-card">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <img 
              src="/login-logo.gif" 
              alt="Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
          </div>
          <CardTitle className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#1d1d1b' }}>
            C30 Dashboard
          </CardTitle>
          <CardDescription className="text-sm md:text-base" style={{ color: '#9d9d9c' }}>
            {isRegister ? 'Create your account' : 'Sign in to access the dashboard'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="fullName" style={{ color: '#1d1d1b' }}>Full Name</Label>
                <Input
                  id="fullName"
                  data-testid="fullname-input"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: '#1d1d1b' }}>Email</Label>
              <Input
                id="email"
                data-testid="username-input"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#1d1d1b' }}>Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-11"
              />
              {!isRegister && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                      style={{ accentColor: '#ee7203' }}
                    />
                    <span className="text-sm text-gray-600">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm hover:underline"
                    style={{ color: '#ee7203' }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" style={{ color: '#1d1d1b' }}>Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  className="h-11"
                />
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-11 text-white font-medium transition-all duration-200"
              style={{ backgroundColor: '#ee7203' }}
              disabled={loading}
            >
              {loading ? (isRegister ? 'Creating Account...' : 'Signing in...') : (isRegister ? 'Create Account' : 'Sign In')}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Button
              variant="ghost"
              onClick={() => {
                setIsRegister(!isRegister);
                setFormData({ email: '', password: '', confirmPassword: '', fullName: '' });
              }}
              data-testid="toggle-register-button"
              className="text-sm"
              style={{ color: '#ee7203' }}
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </Button>
          </div>
        </CardContent>

        {/* Copyright & Terms Links */}
        <div className="text-center mt-4 pb-4">
          <a 
            href="/terms-conditions.html" 
            target="_blank"
            className="text-xs text-gray-500 underline hover:text-gray-700"
          >
            Terms & Conditions
          </a>
        </div>
      </Card>

      {/* Forced Password Change Modal - cannot be closed; shown on first login or after an admin reset */}
      <Dialog open={showForcePwModal} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md bg-white"
          hideCloseButton
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="force-password-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#1d1d1b' }}>
              <Lock className="w-5 h-5" style={{ color: '#ee7203' }} />
              Choose a New Password
            </DialogTitle>
            <DialogDescription>
              For your security you must set your own password before you can continue. Two-factor authentication comes next.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleForcePasswordChange();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="force-pw-new">New Password</Label>
              <Input
                id="force-pw-new"
                data-testid="force-pw-new-input"
                type="password"
                placeholder="At least 12 characters"
                value={forcePwNew}
                onChange={(e) => setForcePwNew(e.target.value)}
                autoComplete="new-password"
                autoFocus
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="force-pw-confirm">Confirm New Password</Label>
              <Input
                id="force-pw-confirm"
                data-testid="force-pw-confirm-input"
                type="password"
                placeholder="Re-enter your new password"
                value={forcePwConfirm}
                onChange={(e) => setForcePwConfirm(e.target.value)}
                autoComplete="new-password"
                className="h-11"
              />
            </div>
            <div className="space-y-1 text-xs">
              <div className={`flex items-center gap-2 ${forcePwNew.length >= 12 ? 'text-green-600' : 'text-gray-500'}`}>
                <Check className="w-3.5 h-3.5" />
                At least 12 characters
              </div>
              <div className={`flex items-center gap-2 ${forcePwConfirm.length > 0 && forcePwNew === forcePwConfirm ? 'text-green-600' : 'text-gray-500'}`}>
                <Check className="w-3.5 h-3.5" />
                Both passwords match
              </div>
              <p className="text-gray-500 pt-1">
                Avoid common passwords and anything containing your name or email address.
              </p>
            </div>
            <Button
              type="submit"
              data-testid="force-pw-submit-button"
              disabled={forcePwLoading || forcePwNew.length < 12 || forcePwNew !== forcePwConfirm}
              className="w-full h-11 text-white font-medium"
              style={{ backgroundColor: '#ee7203' }}
            >
              {forcePwLoading ? 'Saving...' : 'Set Password & Continue'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: '#1d1d1b' }}>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="h-11"
              />
            </div>
            <Button 
              onClick={handleForgotPassword}
              disabled={resetLoading}
              className="w-full h-11"
              style={{ backgroundColor: '#ee7203' }}
            >
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal (triggered by email link) */}
      <Dialog open={showResetModal} onOpenChange={(open) => {
        setShowResetModal(open);
        if (!open) {
          window.history.replaceState({}, document.title, '/');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: '#1d1d1b' }}>Set New Password</DialogTitle>
            <DialogDescription>
              Enter your new password below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="h-11"
              />
            </div>
            <Button 
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="w-full h-11"
              style={{ backgroundColor: '#ee7203' }}
            >
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* MFA Verification Modal */}
      <Dialog open={showMfaModal} onOpenChange={(open) => {
        // Only allow closing via the X button, not by clicking outside
        if (!open) {
          setShowMfaModal(false);
          setMfaCode('');
          setMfaMethod('app');
          setEmailCodeSent(false);
        }
      }}>
        <DialogContent 
          className="sm:max-w-md bg-white"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Red X close button */}
          <button
            onClick={() => {
              setShowMfaModal(false);
              setMfaCode('');
              setMfaMethod('app');
              setEmailCodeSent(false);
            }}
            className="absolute right-4 top-4 p-1 rounded-full hover:bg-red-100 transition-colors group"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-red-500 group-hover:text-red-700" />
          </button>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#1d1d1b' }}>
              <Shield className="w-5 h-5 text-blue-600" />
              Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Choose how you'd like to verify your identity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Method Selection */}
            <div className="flex gap-2">
              {authenticatorEnabled && (
                <button
                  onClick={() => { setMfaMethod('app'); setMfaCode(''); }}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    mfaMethod === 'app' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Smartphone className={`w-6 h-6 ${mfaMethod === 'app' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${mfaMethod === 'app' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Authenticator App
                  </span>
                </button>
              )}
              <button
                onClick={() => { setMfaMethod('email'); setMfaCode(''); }}
                className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  mfaMethod === 'email' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Mail className={`w-6 h-6 ${mfaMethod === 'email' ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${mfaMethod === 'email' ? 'text-blue-700' : 'text-gray-600'}`}>
                  Email Code
                </span>
              </button>
            </div>
            
            {/* App Method */}
            {mfaMethod === 'app' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Enter code from your authenticator app</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    placeholder="000000"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  You can also use one of your backup codes if you can't access your authenticator app.
                </p>
              </>
            )}
            
            {/* Email Method */}
            {mfaMethod === 'email' && (
              <>
                {!emailCodeSent ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600 mb-4">
                      We'll send a 6-digit code to your registered email address.
                    </p>
                    <Button
                      onClick={handleSendEmailCode}
                      disabled={sendingEmailCode}
                      className="gap-2"
                      style={{ backgroundColor: '#ee7203' }}
                    >
                      {sendingEmailCode ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          Send Code to Email
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="mfa-email-code">Enter code from your email</Label>
                      <button
                        onClick={handleSendEmailCode}
                        disabled={sendingEmailCode}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {sendingEmailCode ? 'Sending...' : 'Resend code'}
                      </button>
                    </div>
                    <Input
                      id="mfa-email-code"
                      type="text"
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-12 text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                    <p className="text-xs text-gray-500">
                      Check your inbox for the 6-digit code. It expires in 10 minutes.
                    </p>
                  </div>
                )}
              </>
            )}
            
            {/* Trust Device Option - show when code entry is ready */}
            {(mfaMethod === 'app' || (mfaMethod === 'email' && emailCodeSent)) && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-blue-300"
                    style={{ accentColor: '#3B82F6' }}
                  />
                  <div>
                    <span className="text-sm font-medium text-blue-800">Trust this device for 30 days</span>
                    <p className="text-xs text-blue-600 mt-0.5">
                      You won't need to enter a code on this browser for the next 30 days
                    </p>
                  </div>
                </label>
              </div>
            )}
            
            {/* Verify Button - show when code entry is ready */}
            {(mfaMethod === 'app' || (mfaMethod === 'email' && emailCodeSent)) && (
              <Button 
                onClick={handleMfaVerify}
                disabled={mfaLoading || mfaCode.length < 6}
                className="w-full h-11"
                style={{ backgroundColor: '#ee7203' }}
              >
                {mfaLoading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* MFA Setup Modal */}
      <Dialog open={showMfaSetup} onOpenChange={(open) => {
        // Only allow closing via the X button, not by clicking outside
        if (!open) {
          setShowMfaSetup(false);
          setMfaSetupData(null);
          setMfaSetupCode('');
          setMfaSetupMethod('app');
          setEmailCodeSent(false);
        }
      }}>
        <DialogContent 
          className="sm:max-w-lg bg-white"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          {/* Red X close button */}
          <button
            onClick={() => {
              setShowMfaSetup(false);
              setMfaSetupData(null);
              setMfaSetupCode('');
              setMfaSetupMethod('app');
              setEmailCodeSent(false);
            }}
            className="absolute right-4 top-4 p-1 rounded-full hover:bg-red-100 transition-colors group"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-red-500 group-hover:text-red-700" />
          </button>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#1d1d1b' }}>
              <ShieldCheck className="w-5 h-5 text-green-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              MFA is required for your account. Choose how you'd like to verify your identity.
            </DialogDescription>
          </DialogHeader>
          
          {mfaSetupData && !mfaSetupData.showBackupCodes ? (
            <div className="space-y-4 pt-4">
              {/* Method Selection */}
              <div className="flex gap-2">
                {authenticatorEnabled && (
                  <button
                    onClick={() => { setMfaSetupMethod('app'); setMfaSetupCode(''); setEmailCodeSent(false); }}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                      mfaSetupMethod === 'app' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className={`w-6 h-6 ${mfaSetupMethod === 'app' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${mfaSetupMethod === 'app' ? 'text-blue-700' : 'text-gray-600'}`}>
                      Authenticator App
                    </span>
                    <span className="text-xs text-gray-500">Recommended</span>
                  </button>
                )}
                <button
                  onClick={() => { setMfaSetupMethod('email'); setMfaSetupCode(''); }}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    mfaSetupMethod === 'email' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Mail className={`w-6 h-6 ${mfaSetupMethod === 'email' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium ${mfaSetupMethod === 'email' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Email Code
                  </span>
                  <span className="text-xs text-gray-500">Simpler option</span>
                </button>
              </div>
              
              {/* Authenticator App Setup */}
              {mfaSetupMethod === 'app' && (
                <>
                  {/* Google Authenticator recommendation */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <img src="https://www.gstatic.com/images/branding/product/2x/authenticator_48dp.png" alt="Google Authenticator" className="w-6 h-6" />
                      <span className="font-medium text-blue-800">Use Google Authenticator</span>
                    </div>
                    <p className="text-xs text-blue-600">
                      This is the same authenticator app used for Staffology login. If you already have it installed, simply add this account.
                    </p>
                    <div className="flex gap-2 mt-2">
                      <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline">
                        Download for iOS
                      </a>
                      <span className="text-blue-300">|</span>
                      <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline">
                        Download for Android
                      </a>
                    </div>
                  </div>
                  
                  {/* QR Code */}
                  <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                    {mfaSetupData.qr_code ? (
                      <img src={mfaSetupData.qr_code} alt="MFA QR Code" className="w-48 h-48" />
                    ) : (
                      <div className="w-48 h-48 bg-gray-200 animate-pulse rounded" />
                    )}
                  </div>
                  
                  {/* Manual entry secret */}
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
                    <code className="text-sm bg-gray-100 px-3 py-1 rounded font-mono select-all">
                      {mfaSetupData.secret}
                    </code>
                  </div>
                  
                  {/* Verification input */}
                  <div className="space-y-2">
                    <Label htmlFor="mfa-setup-code">Enter code from app to verify</Label>
                    <Input
                      id="mfa-setup-code"
                      type="text"
                      placeholder="000000"
                      value={mfaSetupCode}
                      onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-12 text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleMfaSetupConfirm}
                    disabled={mfaLoading || mfaSetupCode.length < 6}
                    className="w-full h-11"
                    style={{ backgroundColor: '#ee7203' }}
                  >
                    {mfaLoading ? 'Enabling MFA...' : 'Enable MFA & Continue'}
                  </Button>
                </>
              )}
              
              {/* Email Code Setup */}
              {mfaSetupMethod === 'email' && (
                <>
                  {!emailCodeSent ? (
                    <div className="text-center py-4">
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4 text-left">
                        <p className="text-sm text-blue-700">
                          We'll send a 6-digit verification code to your email address each time you log in.
                        </p>
                      </div>
                      <Button
                        onClick={handleSendSetupEmailCode}
                        disabled={sendingEmailCode}
                        className="gap-2"
                        style={{ backgroundColor: '#ee7203' }}
                      >
                        {sendingEmailCode ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            Send Code to My Email
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700">
                          A code has been sent to your email. Enter it below to enable Email MFA.
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="mfa-setup-email-code">Enter code from your email</Label>
                          <button
                            onClick={handleSendSetupEmailCode}
                            disabled={sendingEmailCode}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {sendingEmailCode ? 'Sending...' : 'Resend code'}
                          </button>
                        </div>
                        <Input
                          id="mfa-setup-email-code"
                          type="text"
                          placeholder="000000"
                          value={mfaSetupCode}
                          onChange={(e) => setMfaSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="h-12 text-center text-2xl tracking-widest font-mono"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-500">
                          Check your inbox for the 6-digit code. It expires in 10 minutes.
                        </p>
                      </div>
                      
                      {/* Trust Device Option */}
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={trustDevice}
                            onChange={(e) => setTrustDevice(e.target.checked)}
                            className="w-4 h-4 mt-0.5 rounded border-blue-300"
                            style={{ accentColor: '#3B82F6' }}
                          />
                          <div>
                            <span className="text-sm font-medium text-blue-800">Trust this device for 30 days</span>
                            <p className="text-xs text-blue-600 mt-0.5">
                              You won't need to enter a code on this browser for the next 30 days
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      <Button 
                        onClick={handleVerifySetupEmailCode}
                        disabled={mfaLoading || mfaSetupCode.length < 6}
                        className="w-full h-11"
                        style={{ backgroundColor: '#ee7203' }}
                      >
                        {mfaLoading ? 'Enabling Email MFA...' : 'Enable Email MFA & Continue'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          ) : mfaSetupData?.showBackupCodes ? (
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <ShieldCheck className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-green-700">MFA Enabled Successfully!</p>
              </div>
              
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-4 h-4 text-yellow-700" />
                  <span className="font-semibold text-yellow-800">Save Your Backup Codes</span>
                </div>
                <p className="text-sm text-yellow-700 mb-3">
                  Store these codes safely. You can use them to access your account if you lose access to your authenticator app.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {mfaSetupData.backupCodes?.map((code, idx) => (
                    <code key={idx} className="text-sm bg-white px-2 py-1 rounded font-mono text-center border">
                      {code}
                    </code>
                  ))}
                </div>
              </div>
              
              <Button 
                onClick={completeMfaSetupLogin}
                className="w-full h-11"
                style={{ backgroundColor: '#ee7203' }}
              >
                I've Saved My Codes - Continue to Dashboard
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Loading MFA setup...
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
